import api from "@/lib/api";

export interface CreatePaymentRequest {
  transaction_amount: number;
  payment_method_id: "pix" | "credit_card" | "debit_card" | "ticket";
  description: string;
  payer: {
    name: string;
    email: string;
    identification?: {
      type: string;
      number: string;
    };
    phone?: {
      area_code: string;
      number: string;
    };
  };
  card?: {
    token: string;
    payment_method_id?: string;
    issuer_id?: string;
    installments?: number;
    installments_cost?: string;
  };
  external_reference?: string;
  metadata?: Record<string, any>;
  notification_url?: string;
  back_url?: string;
  gameId?: string;
  gameParticipantId?: string;
  tournamentId?: string;
  tournamentRegistrationId?: string;
  trainingSessionId?: string;
  trainingEnrollmentId?: string;
  clientId?: string;
  deviceSessionId?: string;
}

export interface ProviderPaymentResponse {
  id: number;
  status: string;
  status_detail?: string;
  transaction_amount?: number;
  payment_method_id?: string;
  description?: string;
  date_created?: string;
  date_approved?: string;
  date_of_expiration?: string;
  qr_code?: string;
  qr_code_base64?: string;
  ticket_url?: string;
  external_reference?: string;
  metadata?: Record<string, any>;
  point_of_interaction?: {
    type: string;
    transaction_data?: {
      qr_code?: string;
      qr_code_base64?: string;
      date_of_expiration?: string;
    };
  };
  raw?: ProviderPaymentResponse;
}

export interface PaymentRecord {
  id: string;
  status: string;
  amount: number;
  method: string;
  providerPaymentId?: string;
}

export interface PaymentReceipt {
  id: string;
  type: "payment" | "refund";
  amount: number;
  method: string;
  status: string;
  paidAt: string | null;
  refundedAt: string | null;
  payerName: string;
  payerEmail: string;
  gameName: string | null;
  gameDate: string | null;
  provider?: string;
  providerPaymentId: string | null;
  providerRefundId: string | null;
  createdAt: string;
  /** Dados do integrador (payment response) */
  providerRaw?: Record<string, unknown> | null;
}

export interface PaymentCreateResponse {
  payment: PaymentRecord;
  provider: ProviderPaymentResponse & { raw?: any };
}

export interface CreateCardPaymentRequest {
  transaction_amount: number;
  description: string;
  email: string;
  name?: string;
  identificationType?: string;
  identificationNumber?: string;
  payerAreaCode?: string;
  payerNumber?: string;
  card: {
    token: string;
    payment_method_id?: string;
    issuer_id?: string;
  };
  external_reference?: string;
  metadata?: Record<string, any>;
  installments?: number;
  gameId?: string;
  gameParticipantId?: string;
  tournamentId?: string;
  tournamentRegistrationId?: string;
  trainingSessionId?: string;
  trainingEnrollmentId?: string;
  clientId?: string;
  deviceSessionId?: string;
}

export interface SaveCardRequest {
  token: string;
  nickname?: string;
}

export interface SavedCard {
  id: string;
  paymentMethodId?: string | null;
  lastFourDigits?: string | null;
  firstSixDigits?: string | null;
  expirationMonth?: number | null;
  expirationYear?: number | null;
  cardholderName?: string | null;
  nickname?: string | null;
  createdAt: string;
}

export interface CreateSavedCardPaymentRequest {
  transaction_amount: number;
  description: string;
  email: string;
  name?: string;
  identificationType?: string;
  identificationNumber?: string;
  payerAreaCode?: string;
  payerNumber?: string;
  external_reference?: string;
  metadata?: Record<string, any>;
  installments?: number;
  gameId?: string;
  gameParticipantId?: string;
  tournamentId?: string;
  tournamentRegistrationId?: string;
  trainingSessionId?: string;
  trainingEnrollmentId?: string;
  clientId?: string;
  /** Código de segurança (CVV) - obrigatório para pagar com cartão salvo */
  securityCode?: string;
  deviceSessionId?: string;
}

export class PaymentService {
  async createPayment(
    data: CreatePaymentRequest,
  ): Promise<PaymentCreateResponse> {
    const response = await api.post<PaymentCreateResponse>("/payments", data);
    return response.data;
  }

  async createCardPayment(
    data: CreateCardPaymentRequest,
  ): Promise<PaymentCreateResponse> {
    const { getDeviceSessionId } = await import("@/services/mercadopago.service");
    const deviceSessionId = data.deviceSessionId ?? (await getDeviceSessionId());
    const response = await api.post<PaymentCreateResponse>(
      "/payments/card",
      { ...data, deviceSessionId },
    );
    return response.data;
  }

  async getPayment(paymentId: number): Promise<ProviderPaymentResponse> {
    const response = await api.get<ProviderPaymentResponse>(
      `/payments/${paymentId}`,
    );
    return response.data;
  }

  async getPaymentsByReference(
    externalReference: string,
  ): Promise<{ payments: ProviderPaymentResponse[]; count: number }> {
    const response = await api.get<{
      payments: ProviderPaymentResponse[];
      count: number;
    }>(`/payments`, {
      params: { external_reference: externalReference },
    });
    return response.data;
  }

  /** Verifica se o usuário já possui pagamento aprovado para o jogo */
  async hasApprovedPayment(gameId: string): Promise<boolean> {
    const response = await api.get<{ hasApproved: boolean }>(
      `/payments/has-approved-payment`,
      { params: { gameId } },
    );
    return response.data.hasApproved;
  }

  async hasApprovedPaymentForTournamentRegistration(
    tournamentRegistrationId: string,
  ): Promise<boolean> {
    const response = await api.get<{ hasApproved: boolean }>(
      `/payments/has-approved-payment`,
      { params: { tournamentRegistrationId } },
    );
    return response.data.hasApproved;
  }

  async hasApprovedPaymentForTrainingEnrollment(
    trainingEnrollmentId: string,
  ): Promise<boolean> {
    const response = await api.get<{ hasApproved: boolean }>(
      `/payments/has-approved-payment`,
      { params: { trainingEnrollmentId } },
    );
    return response.data.hasApproved;
  }

  /** Reprocessa confirmação do pagamento (útil quando pagou para vários mas só aparece um na lista) */
  async reprocessPaymentForGame(gameId: string): Promise<{ success: boolean }> {
    const response = await api.post<{ success: boolean }>(
      `/payments/reprocess-game/${gameId}`,
    );
    return response.data;
  }

  /** [DEV] Simula PIX pago: marca pendente como PAID e confirma participante */
  async simulatePixPaid(gameId: string): Promise<{ success: boolean; paymentId: string }> {
    const response = await api.post<{ success: boolean; paymentId: string }>(
      `/payments/simulate-paid/${gameId}`,
    );
    return response.data;
  }

  /** [DEV] Simula PIX pago e confirma inscrição no torneio */
  async simulatePixPaidForTournamentRegistration(
    tournamentRegistrationId: string,
  ): Promise<{ success: boolean; paymentId: string }> {
    const response = await api.post<{ success: boolean; paymentId: string }>(
      `/payments/simulate-paid-tournament-registration/${tournamentRegistrationId}`,
    );
    return response.data;
  }

  /** [DEV] Simula PIX pago e confirma inscrição de treinamento */
  async simulatePixPaidForTrainingEnrollment(
    trainingEnrollmentId: string,
  ): Promise<{ success: boolean; paymentId: string }> {
    const response = await api.post<{ success: boolean; paymentId: string }>(
      `/payments/simulate-paid-training-enrollment/${trainingEnrollmentId}`,
    );
    return response.data;
  }

  /** Busca pagamento PIX pendente para o jogo (tabela payments, external_reference = payment.id) */
  async getPendingPixForGame(gameId: string): Promise<{
    payment: PaymentRecord | null;
    provider: ProviderPaymentResponse | null;
  }> {
    const response = await api.get<{
      payment: PaymentRecord | null;
      provider: ProviderPaymentResponse | null;
    }>(`/payments/pending-pix`, {
      params: { gameId },
    });
    return response.data;
  }

  async getPendingPixForTournamentRegistration(tournamentRegistrationId: string): Promise<{
    payment: PaymentRecord | null;
    provider: ProviderPaymentResponse | null;
  }> {
    const response = await api.get<{
      payment: PaymentRecord | null;
      provider: ProviderPaymentResponse | null;
    }>(`/payments/pending-pix`, {
      params: { tournamentRegistrationId },
    });
    return response.data;
  }

  async getPendingPixForTrainingEnrollment(trainingEnrollmentId: string): Promise<{
    payment: PaymentRecord | null;
    provider: ProviderPaymentResponse | null;
  }> {
    const response = await api.get<{
      payment: PaymentRecord | null;
      provider: ProviderPaymentResponse | null;
    }>(`/payments/pending-pix`, {
      params: { trainingEnrollmentId },
    });
    return response.data;
  }

  async cancelPayment(paymentId: number): Promise<ProviderPaymentResponse> {
    const response = await api.put<ProviderPaymentResponse>(
      `/payments/${paymentId}/cancel`,
    );
    return response.data;
  }

  async refundPayment(paymentId: number, amount?: number): Promise<any> {
    const response = await api.post(
      `/payments/${paymentId}/refund`,
      amount ? { amount } : {},
    );
    return response.data;
  }

  async saveCard(data: SaveCardRequest): Promise<SavedCard> {
    const response = await api.post<SavedCard>("/payments/cards", data);
    return response.data;
  }

  async listCards(): Promise<SavedCard[]> {
    const response = await api.get<SavedCard[]>("/payments/cards");
    return response.data;
  }

  async updateCardNickname(id: string, nickname: string): Promise<SavedCard> {
    const response = await api.patch<SavedCard>(`/payments/cards/${id}`, {
      nickname: nickname.trim().slice(0, 50),
    });
    return response.data;
  }

  async deleteCard(id: string): Promise<{ deleted: boolean }> {
    const response = await api.delete<{ deleted: boolean }>(
      `/payments/cards/${id}`,
    );
    return response.data;
  }

  async payWithSavedCard(
    cardId: string,
    data: CreateSavedCardPaymentRequest,
  ): Promise<PaymentCreateResponse> {
    const { getDeviceSessionId } = await import("@/services/mercadopago.service");
    const deviceSessionId = data.deviceSessionId ?? (await getDeviceSessionId());
    const response = await api.post<PaymentCreateResponse>(
      `/payments/cards/${cardId}/pay`,
      { ...data, deviceSessionId },
    );
    return response.data;
  }

  async getReceipt(paymentId: string): Promise<PaymentReceipt> {
    const response = await api.get<PaymentReceipt>(
      `/payments/receipt/${paymentId}`,
    );
    return response.data;
  }

  /** Consulta status do reembolso no Mercado Pago (dados frescos). */
  async getRefundStatus(paymentId: string): Promise<Record<string, unknown>> {
    const response = await api.get(`/payments/${paymentId}/refund-status`);
    return response.data;
  }
}

export const paymentService = new PaymentService();
